import React from 'react'

import { Form, Header, InputOnChangeData, Segment, Dropdown, Message } from 'semantic-ui-react'

import { IPlan, IProject, ProjectQuestionResponse } from '../../../api/Data'

import CustomLabel from '../../../components/common/custom-label/CustomLabel'
import UpgradeTag from '../../../components/common/UpgradeTag'
import ExternalLink from '../../../components/common/ExternalLink'
import {
  findDuplicateVideoPromptFields,
  isVideoPromptRequired,
  setVideoPromptRequired,
  trimOptionalVideoPromptIndexes,
} from '../../../utils/videoPrompts'

const MAX_NUMBER_OF_QUESTIONS = 8
const MAX_QUESTIONS_CHARACTER_LENGTH = 260

const CollectTabQuestionsPage = (props: IProps) => {
  // Props
  const { project, plan, projectQuestions, handleChange } = props
  const isCaptureWorkflow = project.projectType === 'video_capture'

  // Dropdown options should be 1 to 8
  const dropdownOptions = Array.from(Array(MAX_NUMBER_OF_QUESTIONS).keys()).map((i) => ({
    key: i + 1,
    text: i + 1,
    value: i + 1,
  }))

  const handleDropdownChange = (e: React.SyntheticEvent<HTMLElement, Event>, data: any) => {
    handleChange(e, {
      projectChanges: {
        numberOfQuestions: data.value,
        displaySetting: trimOptionalVideoPromptIndexes(project.displaySetting, Number(data.value)),
      },
    })
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => {
    const { name, value } = data

    // Get the question number from the name
    const questionNumber = name.split('-')[1]

    // Copy the questions array
    const updatedQuestions = [...project.questions]

    // Update the specific question
    updatedQuestions[questionNumber - 1] = value

    // Update the project
    handleChange(e, {
      name: 'questions',
      values: [...updatedQuestions],
    })
  }

  const handlePromptRequiredChange = (e: React.FormEvent<HTMLInputElement>, promptIndex: number, required: boolean) => {
    handleChange(e, {
      name: 'displaySetting',
      value: setVideoPromptRequired(project.displaySetting, promptIndex, required),
    })
  }

  const contributorQuestions = projectQuestions.filter((question) => !question.isAskedPerClip)
  const duplicatePromptFields = findDuplicateVideoPromptFields(
    project.questions.slice(0, project.numberOfQuestions),
    contributorQuestions
  )

  return (
    <>
      <Header as="h1" style={{ marginBottom: '2rem', fontWeight: 'bold' }}>
        Video Survey Builder
        {!plan.isAllowedMultiClipUpload && (
          <UpgradeTag text="Upgrade your plan to ask your contributor multiple questions" />
        )}
        <Header.Subheader>
          {isCaptureWorkflow
            ? 'Set the on-screen prompt contributors answer with one recorded video response.'
            : 'Build a video conversation with two to eight prompts. Contributors record a separate response to each prompt.'}
        </Header.Subheader>
      </Header>

      <Header as="h4">
        <strong>
          This page is used only by Capture and Conversation workflows. Other workflows skip it automatically.
        </strong>
      </Header>

      <Segment>
        <Header as="h2" style={{ fontWeight: 'bold' }}>
          Video Prompts
        </Header>

        <Header as="h4">
          {isCaptureWorkflow
            ? 'Capture uses one prompt and one camera response.'
            : 'Conversation supports two to eight prompts, each answered with a separate video response.'}
          &nbsp;
          <ExternalLink url="https://vloggi.com/docs/building-a-video-questionnaire-in-vloggi-enhancing-engagement-and-insight/">
            Read more.
          </ExternalLink>
        </Header>

        {duplicatePromptFields.length > 0 && (
          <Message warning>
            <Message.Header>Repeated contributor questions</Message.Header>
            <p>
              Contributors will answer these in both video and text:{' '}
              {duplicatePromptFields
                .map(({ promptIndex, fieldText }) => `Prompt ${promptIndex + 1} — ${fieldText}`)
                .join('; ')}
              . Keep both only when you intentionally need both answer formats.
            </p>
          </Message>
        )}

        <Segment>
          <Form>
            <Form.Field>
              <Dropdown
                fluid
                selection
                options={dropdownOptions}
                defaultValue={project.numberOfQuestions ?? 1}
                onChange={handleDropdownChange}
                disabled={isCaptureWorkflow || !plan.isAllowedMultiClipUpload}
              />
            </Form.Field>
          </Form>
        </Segment>

        <Header as="h4" style={{ fontWeight: 'bold' }}>
          Question text
        </Header>

        <Form>
          {Array.from(Array(project.numberOfQuestions).keys()).map((i) => (
            <Form.Field key={i}>
              <CustomLabel>
                <CustomLabel.Text text={'Prompt ' + (i + 1)} />
                <CustomLabel.WordCount word={project.questions[i]} limit={MAX_QUESTIONS_CHARACTER_LENGTH} />
              </CustomLabel>
              <Form.Input
                placeholder="Type a question here..."
                name={'question-' + (i + 1)}
                maxLength={MAX_QUESTIONS_CHARACTER_LENGTH}
                value={project.questions[i] || ''}
                onChange={handleInputChange}
                disabled={!plan.isAllowedMultiClipUpload}
              />
              {!isCaptureWorkflow && (
                <Form.Checkbox
                  toggle
                  checked={isVideoPromptRequired(project.displaySetting, i)}
                  label={
                    isVideoPromptRequired(project.displaySetting, i)
                      ? 'Required response'
                      : 'Optional — contributors may skip'
                  }
                  onChange={(event, data) => handlePromptRequiredChange(event, i, Boolean(data.checked))}
                  disabled={!plan.isAllowedMultiClipUpload}
                />
              )}
            </Form.Field>
          ))}
        </Form>
      </Segment>
    </>
  )
}

interface IProps {
  project: IProject
  plan: IPlan
  projectQuestions: ProjectQuestionResponse[]
  handleChange: (arg: React.SyntheticEvent, data: any) => void
}

export default CollectTabQuestionsPage
